import cn from 'classnames';
import type { ErrorDetails } from '#api/errors.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { LinkButton } from '#components/atoms/buttons/link-button/LinkButton.tsx';
import type { LogStatus } from '../../worker/log-worker-types.ts';
import { DebugCommandMenu, type CommandModal } from '../DebugCommandMenu.tsx';
import styles from './DebugEmpty.module.css';

type Props = {
  errors: ErrorDetails[];
  supportedCommands: string[];
  onStartStream: () => void;
  onDebugStack: () => void;
  status?: LogStatus;
  setCommandModal: (modalName: CommandModal) => void;
};

/**
 * Display a message and usually a button in the middle of the debug console, when there are no
 * logs to show.
 */
export function DebugEmpty({ status, errors, supportedCommands, onStartStream, onDebugStack, setCommandModal }: Props) {
  if (status === 'idle') {
    return (
      <div className={cn('center flex-col gap-8', styles.DebugEmpty)}>
        <div className="flex gap-8">
          <Button variant="secondary" onClick={onStartStream} disabled={!status}>
            Stream logs
          </Button>
          <DebugCommandMenu
            variant="button"
            supportedCommands={supportedCommands}
            setCommandModal={setCommandModal}
            onStartStream={onStartStream}
            onDebugStack={onDebugStack}
          />
        </div>
        <p className="p-sm mb-0">Session expires after 5 minutes</p>
      </div>
    );
  }

  if (status === 'error') {
    if (errors.length && errors.some((e) => e.code === 'ERR_OUTDATED_DNCLIENT')) {
      return (
        <div className={cn('center flex-col gap-8', styles.DebugEmpty)}>
          <p className="h5 mb-16">Client version 0.6.0 required to debug</p>

          <LinkButton variant="secondary" to="/downloads">
            Download update
          </LinkButton>
        </div>
      );
    }

    if (errors.length && errors.some((e) => e.code === 'ERR_DNCLIENT_NOT_REACHABLE')) {
      return (
        <div className={cn('center flex-col gap-8', styles.DebugEmpty)}>
          <p className="h5 mb-8">Unable to connect</p>
          <p className="p-md mb-16">Please be sure dnclient is running on the host</p>

          <Button variant="secondary" onClick={onStartStream}>
            Retry
          </Button>
        </div>
      );
    }

    return (
      <div className={cn('center flex-col gap-8', styles.DebugEmpty)}>
        <p className="h5 mb-16">There was a problem streaming log messages</p>

        <LinkButton variant="secondary" to="/support/contact">
          Contact support
        </LinkButton>
      </div>
    );
  }
}
